import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';

export function usePersistentState<T>(key: string, initial: T, validate?: (value: unknown) => value is T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValueState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(`fbdash:ui:v1:${key}`);
      if (raw !== null) {
        const stored: unknown = JSON.parse(raw);
        if (validate ? validate(stored) : sameShape(stored, initial)) return stored as T;
      }
    } catch { /* Storage may be unavailable or corrupted. */ }
    return initial;
  });
  const setValue = useCallback<Dispatch<SetStateAction<T>>>((action) => {
    const persist = (next: T) => {
      try { localStorage.setItem(`fbdash:ui:v1:${key}`, JSON.stringify(next)); } catch { /* Keep the UI usable without storage. */ }
      return next;
    };
    // Direct selections (including report tabs) persist within the click event,
    // before a reload can interrupt a deferred effect.
    if (typeof action !== 'function') setValueState(persist(action));
    else setValueState((previous) => persist((action as (previous: T) => T)(previous)));
  }, [key]);
  return [value, setValue];
}

function sameShape(value: unknown, initial: unknown): boolean {
  if (initial === null) return value === null || typeof value === 'string' || typeof value === 'number';
  if (Array.isArray(initial)) return Array.isArray(value) && value.every((item) => typeof item === 'string');
  if (typeof initial === 'object') return value !== null && typeof value === 'object' && !Array.isArray(value)
    && Object.entries(initial).every(([key, field]) => sameShape((value as Record<string, unknown>)[key], field));
  return typeof value === typeof initial;
}
